import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Store as ChantierIcon, ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon, AttachMoney as MoneyIcon,
  TrendingUp as MarginIcon, LocalShipping as DeliveryIcon,
  ShoppingCart as OrderIcon, Warning as WarningIcon,
  CheckCircle as DoneIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmChantier, ChantierStatus, DeliveryStatus, CrmOrganisation, CrmOpportunity } from '../types/crm';

const emptyChantier = (): Partial<CrmChantier> => ({
  title: '',
  clientName: '',
  address: '',
  wilaya: 'Alger',
  status: 'DEMARRAGE',
  startDate: new Date().toISOString().split('T')[0],
  progress: 15,
  kamName: 'Karim Benali',
  ordersCount: 1,
  ordersPending: 1,
  ordersLate: 0,
  deliveryStatus: 'ON_SCHEDULE',
  totalCa: 5000000,
  marginPct: 25,
  collectedPct: 30,
  satisfactionScore: 85
});

const getStatusColor = (status: ChantierStatus): 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'DEMARRAGE': return 'info';
    case 'EN_COURS': return 'primary';
    case 'POSE': return 'warning';
    case 'RECEPTION': return 'success';
    case 'CLOTURE': return 'default';
    case 'ARRETE': return 'error';
    default: return 'default';
  }
};

const getDeliveryColor = (status: DeliveryStatus): 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'ON_SCHEDULE': return 'info';
    case 'PARTIALLY_DELIVERED': return 'warning';
    case 'DELIVERED': return 'success';
    case 'DELAYED': return 'error';
    default: return 'info';
  }
};

export const ChantiersPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [chantiersList, setChantiersList] = useState<CrmChantier[]>([]);
  const [organisations, setOrganisations] = useState<CrmOrganisation[]>([]);
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmChantier>>(emptyChantier());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [chData, oData, oppData] = await Promise.all([
        crmApi.getChantiers(),
        crmApi.getOrganisations(),
        crmApi.getOpportunities()
      ]);
      setChantiersList(chData);
      setOrganisations(oData);
      setOpportunities(oppData);
    } catch (err) {
      console.error('Error loading CRM chantiers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyChantier());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (chantier: CrmChantier) => {
    setFormData({ ...chantier });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (chantier: CrmChantier) => {
    const chantierId = (chantier.id || chantier.CCH_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.chantiers.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce chantier actif ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteChantier(chantierId);
      if (res.success) {
        blobticMessage.success(t('crm.chantiers.deleteSuccess', 'Chantier supprimé avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('crm.chantiers.titleRequired', 'L\'intitulé du chantier est obligatoire.');
    }
    if (!formData.clientName?.trim()) {
      return t('crm.chantiers.clientRequired', 'Le nom du client / maître d\'ouvrage est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CCH_IDF: formData.id || formData.CCH_IDF,
      CCH_CODE: formData.code || formData.CCH_CODE,
      CCH_TITLE: formData.title,
      COP_IDF: formData.projectId || formData.COP_IDF,
      COR_IDF: formData.organisationId || formData.COR_IDF,
      CCT_IDF: formData.contactId || formData.CCT_IDF,
      CCH_CLIENT_NAME: formData.clientName,
      CCH_ADDRESS: formData.address,
      CCH_WILAYA: formData.wilaya,
      CCH_STATUS: formData.status,
      CCH_START_DATE: formData.startDate,
      CCH_ESTIMATED_END_DATE: formData.estimatedEndDate,
      CCH_ACTUAL_END_DATE: formData.actualEndDate,
      CCH_PROGRESS: formData.progress,
      CCH_KAM_NAME: formData.kamName,
      CCH_ORDERS_COUNT: formData.ordersCount,
      CCH_ORDERS_PENDING: formData.ordersPending,
      CCH_ORDERS_LATE: formData.ordersLate,
      CCH_DELIVERY_STATUS: formData.deliveryStatus,
      CCH_TOTAL_CA: formData.totalCa,
      CCH_MARGIN_PCT: formData.marginPct,
      CCH_COLLECTED_PCT: formData.collectedPct,
      CCH_SATISFACTION_SCORE: formData.satisfactionScore,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.chantiers.editTitle', 'Suivi du Chantier Actif') : t('crm.chantiers.newTitle', 'Nouveau Chantier KAM')}
        icon={<ChantierIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_chantier', primaryKey: 'CCH_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Cadrage Chantier */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChantierIcon color="primary" /> {t('crm.chantiers.secIdent', 'Identification du Chantier & Affaire')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('common.code', 'Code Chantier')}
                      value={formData.code || t('common.autoGenerated', '(Automatique)')}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.chantiers.titleField', 'Intitulé du Chantier')}
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('common.status', 'Phase / Statut')}
                      value={formData.status || 'DEMARRAGE'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as ChantierStatus })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="DEMARRAGE">{t('crm.chantiers.statusDemarrage', 'En Démarrage (Préparation)')}</MenuItem>
                      <MenuItem value="EN_COURS">{t('crm.chantiers.statusEnCours', 'En Cours d\'Exécution')}</MenuItem>
                      <MenuItem value="POSE">{t('crm.chantiers.statusPose', 'En Cours de Pose Menuiseries')}</MenuItem>
                      <MenuItem value="RECEPTION">{t('crm.chantiers.statusReception', 'Phase Réception / Levée de réserves')}</MenuItem>
                      <MenuItem value="CLOTURE">{t('crm.chantiers.statusCloture', 'Clôturé avec Succès')}</MenuItem>
                      <MenuItem value="ARRETE">{t('crm.chantiers.statusArrete', 'À l\'Arrêt')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.clientName', 'Client / Promoteur')}
                      value={formData.clientName || ''}
                      onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.chantiers.project', 'Projet Initial (Passation)')}
                      value={formData.projectId || ''}
                      onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">-- {t('crm.chantiers.directKamProject', 'Prospection directe KAM (Sans prescription)')} --</MenuItem>
                      {opportunities.map((opp) => (
                        <MenuItem key={opp.id || opp.COP_IDF} value={opp.id || opp.COP_IDF}>
                          {opp.title} ({opp.stage})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.kamName', 'KAM Responsable')}
                      value={formData.kamName || ''}
                      onChange={(e) => setFormData({ ...formData, kamName: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.wilaya', 'Wilaya')}
                      value={formData.wilaya || ''}
                      onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label={t('common.address', 'Adresse Complète du Chantier')}
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Planning & Avancement */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChantierIcon color="primary" /> {t('crm.chantiers.secPlanning', 'Planning & Avancement')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.chantiers.startDate', 'Date Démarrage')}
                      type="date"
                      value={formData.startDate || ''}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.chantiers.estimatedEndDate', 'Échéance Prévisionnelle')}
                      type="date"
                      value={formData.estimatedEndDate || ''}
                      onChange={(e) => setFormData({ ...formData, estimatedEndDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {t('crm.chantiers.operationalProgress', 'Avancement Opérationnel')} : {formData.progress || 0}%
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={formData.progress || 0}
                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                      />
                      <TextField
                        type="number"
                        value={formData.progress ?? 0}
                        onChange={(e) => setFormData({ ...formData, progress: Number(e.target.value) })}
                        sx={{ width: 80 }}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.chantiers.satisfaction', 'Score Satisfaction Client (0-100)')}
                      type="number"
                      value={formData.satisfactionScore ?? 85}
                      onChange={(e) => setFormData({ ...formData, satisfactionScore: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 3: Commandes & Livraisons */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeliveryIcon color="primary" /> {t('crm.chantiers.secOrders', 'Commandes & Livraisons')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.ordersCount', 'Commandes Traitées')}
                      type="number"
                      value={formData.ordersCount ?? 0}
                      onChange={(e) => setFormData({ ...formData, ordersCount: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.ordersPending', 'Commandes En Cours')}
                      type="number"
                      value={formData.ordersPending ?? 0}
                      onChange={(e) => setFormData({ ...formData, ordersPending: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.ordersLate', 'Commandes En Retard')}
                      type="number"
                      value={formData.ordersLate ?? 0}
                      onChange={(e) => setFormData({ ...formData, ordersLate: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label={t('crm.chantiers.deliveryStatus', 'État des Livraisons')}
                      value={formData.deliveryStatus || 'ON_SCHEDULE'}
                      onChange={(e) => setFormData({ ...formData, deliveryStatus: e.target.value as DeliveryStatus })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="ON_SCHEDULE">{t('crm.chantiers.delivOnSchedule', 'Conforme au Planning')}</MenuItem>
                      <MenuItem value="PARTIALLY_DELIVERED">{t('crm.chantiers.delivPartially', 'Partiellement Livré')}</MenuItem>
                      <MenuItem value="DELIVERED">{t('crm.chantiers.delivDelivered', 'Intégralement Livré sur Chantier')}</MenuItem>
                      <MenuItem value="DELAYED">{t('crm.chantiers.delivDelayed', 'Livraison en Retard Usine / Transport')}</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 4: Indicateurs Financiers Order-to-Cash */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" /> {t('crm.chantiers.secFinancial', 'Indicateurs Financiers & Encaissements')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.totalCa', 'Chiffre d\'Affaires Total (DZD)')}
                      type="number"
                      value={formData.totalCa ?? 0}
                      onChange={(e) => setFormData({ ...formData, totalCa: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.marginPct', 'Taux de Marge Réalisée (%)')}
                      type="number"
                      value={formData.marginPct ?? 0}
                      onChange={(e) => setFormData({ ...formData, marginPct: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.chantiers.collectedPct', 'Taux d\'Encaissement / Règlements (%)')}
                      type="number"
                      value={formData.collectedPct ?? 0}
                      onChange={(e) => setFormData({ ...formData, collectedPct: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.chantiers.title', 'Chantiers Actifs & Suivi Opérationnel (KAM)')}
      icon={<ChantierIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {chantiersList.map((chantier) => {
            const chantierId = chantier.id || chantier.CCH_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={chantierId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 44, height: 44 }}>
                        <ChantierIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={chantier.title}>
                          {chantier.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {chantier.code} • KAM: {chantier.kamName || t('crm.chantiers.assigned', 'Attitré')}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(chantier.status, chantier.status)}
                        color={getStatusColor(chantier.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        👤 {t('crm.chantiers.client', 'Client')}: {chantier.clientName}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon fontSize="inherit" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {chantier.address || chantier.wilaya || 'Algérie'}
                        </Typography>
                      </Stack>

                      {/* Barre d'avancement */}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('crm.chantiers.progressTitle', 'Avancement Chantier')}</Typography>
                          <Typography variant="caption" fontWeight={700}>{chantier.progress}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={chantier.progress || 0}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      {/* Suivi Commandes & Livraison */}
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                        <Chip
                          icon={<OrderIcon fontSize="small" />}
                          label={`${chantier.ordersCount} ${t('crm.chantiers.cmdCount', 'cmd(s)')}`}
                          size="small"
                          variant="outlined"
                        />
                        {chantier.ordersLate > 0 && (
                          <Chip
                            icon={<WarningIcon fontSize="small" />}
                            label={`${chantier.ordersLate} ${t('crm.chantiers.enRetard', 'en retard')}`}
                            size="small"
                            color="error"
                          />
                        )}
                        <Chip
                          label={t(chantier.deliveryStatus, chantier.deliveryStatus)}
                          color={getDeliveryColor(chantier.deliveryStatus)}
                          size="small"
                        />
                      </Stack>

                      {/* Métriques Financières */}
                      <Box sx={{ bgcolor: 'action.hover', p: 1.2, borderRadius: 1.5 }}>
                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                          {(chantier.totalCa || 0).toLocaleString()} DZD
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('crm.chantiers.margin', 'Marge')}: <b>{chantier.marginPct}%</b>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('crm.chantiers.collected', 'Encaissé')}: <b>{chantier.collectedPct}%</b>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('crm.chantiers.satisfactionLabel', 'Satisfaction')}: <b>{chantier.satisfactionScore}%</b>
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {chantier.reclamationsCount ? `⚠️ ${chantier.reclamationsCount} ${t('crm.chantiers.reclamationCount', 'réclamation(s)')}` : t('crm.chantiers.zeroReclamation', '0 réclamation')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(chantier)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(chantier)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 130 },
            { id: 'title', label: t('crm.chantiers.titleField', 'Chantier'), minWidth: 220 },
            { id: 'clientName', label: t('crm.chantiers.clientName', 'Client'), width: 180 },
            { id: 'wilaya', label: t('crm.chantiers.wilaya', 'Wilaya'), width: 120 },
            { id: 'kamName', label: t('crm.chantiers.kamName', 'KAM'), width: 140 },
            {
              id: 'status',
              label: t('common.status', 'Statut'),
              width: 140,
              chip: true,
              chipColorMap: {
                DEMARRAGE: 'info',
                EN_COURS: 'primary',
                POSE: 'warning',
                RECEPTION: 'success',
                CLOTURE: 'default',
                ARRETE: 'error'
              }
            },
            { id: 'progress', label: t('crm.chantiers.progress', 'Avancement'), width: 110, format: (val: any) => `${val}%` },
            { id: 'totalCa', label: t('crm.chantiers.totalCa', 'CA Total (DZD)'), width: 160, format: (val: any) => `${Number(val || 0).toLocaleString()} DZD` },
            { id: 'marginPct', label: t('crm.chantiers.marginPct', 'Marge %'), width: 100, format: (val: any) => `${val}%` },
            { id: 'collectedPct', label: t('crm.chantiers.collectedPct', 'Encaissé %'), width: 110, format: (val: any) => `${val}%` },
          ]}
          data={chantiersList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.chantiers.search', 'Rechercher un chantier actif, client, KAM...')}
          filterColumns={['code', 'title', 'clientName', 'wilaya', 'kamName', 'status']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default ChantiersPage;
